import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api";
import { validateEmail, sanitizeText, validatePhone } from "../utils/validator";
import { useLoading } from "../contexts/LoadingContext";
import {
  UsersIcon,
  MailIcon,
  LockIcon,
  EyeIcon,
  EyeOffIcon,
  LogInIcon,
  UserPlusIcon,
} from "../components/icons";

function Login() {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setLoading: setGlobalLoading, setGlobalLoadingMessage } = useLoading();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    setGlobalLoading(true);
    setGlobalLoadingMessage("Signing in...");

    // Validate inputs before sending (accept email or phone)
    const id = sanitizeText(identifier).trim();
    if (!id) {
      setError("Email or phone is required");
      setLoading(false);
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
      return;
    }

    const isEmail = validateEmail(id);
    const isPhone = !isEmail && validatePhone(id);

    if (!isEmail && !isPhone) {
      setError("Enter a valid email address or phone number");
      setLoading(false);
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
      return;
    }

    if (!password || password.length < 6) {
      setError("Password must be at least 6 characters");
      setLoading(false);
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
      return;
    }

    try {
      await apiFetch("/auth/login", {
        method: "POST",
        body: JSON.stringify({
          identifier: id,
          password: sanitizeText(password),
        }),
      });
      // Mark bureau as logged in so other tabs can detect it synchronously.
      // The actual auth still relies on the HttpOnly cookie; this is just a
      // visibility flag for the UI's auto-redirect logic.
      try {
        localStorage.setItem("bureauActive", "true");
      } catch {
        /* localStorage may be unavailable */
      }
      navigate("/bureau/dashboard");
    } catch (err) {
      console.error("Login error:", err);
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
    }
  };

  return (
  <div>
    <div className="flex justify-end ">
       <p className="flex text-sm items-center mt-3">Are you Profile Seeker?</p>
       <button
        type="button"
        onClick={() => navigate("/")}
        className="mt-3 px-2 mx-2 rounded-lg border border-[var(--color-border)] py-2 text-sm font-semibold text-[var(--color-text)] transition hover:bg-[var(--color-surface-hover)]"
      >
        User Login
      </button>
     </div>
      <div className="flex min-h-screen items-center justify-center bg-[var(--color-background)]">
      <div className="w-full max-w-md rounded-3xl border border-[var(--color-border)] bg-[var(--color-surface)] p-8 shadow-xl shadow-[var(--color-primary-soft)]">
        {/* Brand header */}
        <div className="mb-6 flex flex-col items-center text-center">
          <span className="mb-3 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--color-primary-soft)] text-[var(--color-primary)]">
            <UsersIcon className="h-7 w-7" />
          </span>
          <h2 className="text-2xl font-bold text-[var(--color-text-strong)]">
            Marriage Bureau Login
          </h2>
          <p className="mt-1 text-xs text-[var(--color-text-muted)]">
            Sign in to manage your bureau
          </p>
        </div>

        {error && (
          <div className="mb-4 rounded-xl border border-[var(--color-danger-border)] bg-[var(--color-danger-bg)] px-4 py-2 text-[var(--color-danger-text)]">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Email / Phone */}
          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium text-[var(--color-text-muted)]">
              Email or Phone
            </label>
            <div className="relative">
              <span className="pointer-events-none absolute inset-y-0 left-0 flex w-10 items-center justify-center text-[var(--color-text-muted)]">
                <MailIcon className="h-4 w-4" />
              </span>
              <input
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                required
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] py-2 pl-11 pr-3 text-sm text-[var(--color-text-strong)] shadow-sm focus:border-[var(--color-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--color-border-strong)]"
                placeholder="Email or phone number"
              />
            </div>
          </div>

          {/* Password with toggle */}
          <div className="mb-6">
            <label className="mb-2 block text-sm font-medium text-[var(--color-text-muted)]">
              Password
            </label>
            <div className="relative">
              <span className="pointer-events-none absolute inset-y-0 left-0 flex w-10 items-center justify-center text-[var(--color-text-muted)]">
                <LockIcon className="h-4 w-4" />
              </span>
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] py-2 pl-11 pr-10 text-sm text-[var(--color-text-strong)] shadow-sm focus:border-[var(--color-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--color-border-strong)]"
                placeholder="Enter your password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-[var(--color-text-muted)] hover:text-[var(--color-text-strong)] transition"
              >
                {showPassword ? (
                  <EyeOffIcon className="h-4 w-4" />
                ) : (
                  <EyeIcon className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[var(--color-primary)] py-2 text-sm font-semibold text-[var(--color-on-primary)] shadow-sm transition hover:bg-[var(--color-primary-hover)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-soft)] disabled:opacity-50"
          >
            <LogInIcon className="h-4 w-4" />
            {loading ? "Logging in..." : "Login"}
          </button>

          <p className="mt-3 text-center text-sm">
            <button
              type="button"
              onClick={() => navigate("/forgot-password")}
              className="font-medium text-[var(--color-text-muted)] underline-offset-2 hover:underline hover:text-[var(--color-text-strong)]"
            >
              Forgot password?
            </button>
          </p>
        </form>

        <p className="mt-4 flex items-center justify-center gap-1 text-center text-sm text-[var(--color-text)]">
          Don’t have an account?{" "}
          <button
            type="button"
            onClick={() => navigate("/bureau/register")}
            className="inline-flex items-center gap-1 font-semibold text-[var(--color-text)] transition hover:text-[var(--color-text-strong)]"
          >
            <UserPlusIcon className="h-4 w-4" />
            Register here
          </button>
        </p>
      </div>
    </div>
  </div>
  );
}

export default Login;
